import { c as _c } from "react/compiler-runtime";
import React from "react";
import { FormInput } from "@repo/ui/form-input";
import { Textarea } from "@repo/ui/textarea";
import { Select } from "@repo/ui/select";
import { MultiSelect } from "@repo/ui/multi-select";
import { Checkbox } from "@repo/ui/checkbox";
import { RadioGroup } from "@repo/ui/radio";
import { InputDate } from "@repo/ui/FormComponents/InputDate";
import { InputDateRange } from "@repo/ui/FormComponents/InputDateRange";
import { InputTime } from "@repo/ui/FormComponents/InputTime";
import { InputSelectionCard } from "@repo/ui/FormComponents/InputSelectionCard";
import { InputFile } from "@repo/ui/FormComponents/InputFile";
import { InputRepeater, type RepeaterColumn, type RepeaterRow } from "@repo/ui/FormComponents/InputRepeater";
import { InputPhoneNumber } from "@repo/ui/FormComponents/InputPhoneNumber";
import { RequiredMark } from "@repo/ui/required-mark";
import type { PortableTextBlock } from "next-sanity";
import type { FormFieldFromQuery } from "@/lib/types";
import { getPopiConsentText } from "@/lib/form-helpers";
import { SelectionCardDescription } from "./selection-card-description.component";
export interface FormFieldProps {
  field: FormFieldFromQuery;
  fieldApi: {
    name: string;
    value: unknown;
    handleChange: (value: unknown) => void;
    handleBlur: () => void;
    state: {
      meta: {
        errors: string[];
      };
    };
  };
  /** Parent form id — passed through to file uploads as part of the upload token payload. */
  formId?: string;
  /** Parent form wizard id — passed through to file uploads as part of the upload token payload. */
  formWizardId?: string;
}
export const FormFieldRenderer = t0 => {
  const $ = _c(260);
  const {
    field,
    fieldApi,
    formId,
    formWizardId
  } = t0;
  const {
    name,
    value,
    handleChange,
    handleBlur
  } = fieldApi;
  const errors = fieldApi.state.meta.errors;
  const error = errors?.[0];
  const descriptionBlocks = field.description as PortableTextBlock[] | null;
  const hasDescription = Array.isArray(descriptionBlocks) && descriptionBlocks.length > 0;
  let t1;
  if ($[0] !== descriptionBlocks || $[1] !== hasDescription) {
    t1 = hasDescription ? <SelectionCardDescription value={descriptionBlocks} /> : undefined;
    $[0] = descriptionBlocks;
    $[1] = hasDescription;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const descriptionNode = t1;
  switch (field.type) {
    case "textarea":
      {
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        const t4 = field.label || "";
        const t5 = field.placeholder ?? undefined;
        const t6 = String(value ?? "");
        let t7;
        if ($[3] !== handleChange) {
          t7 = e_2 => handleChange(e_2.target.value);
          $[3] = handleChange;
          $[4] = t7;
        } else {
          t7 = $[4];
        }
        const t8 = field.characterLimit || undefined;
        const t9 = !!field.characterLimit;
        const t10 = field.required ?? undefined;
        let t11;
        if ($[5] !== descriptionNode || $[6] !== error || $[7] !== handleBlur || $[8] !== t10 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
          t11 = <Textarea id={t2} name={t3} label={t4} placeholder={t5} helperText={descriptionNode} errorText={error} value={t6} onChange={t7} onBlur={handleBlur} maxLength={t8} showCount={t9} required={t10} />;
          $[5] = descriptionNode;
          $[6] = error;
          $[7] = handleBlur;
          $[8] = t10;
          $[9] = t2;
          $[10] = t3;
          $[11] = t4;
          $[12] = t5;
          $[13] = t6;
          $[14] = t7;
          $[15] = t8;
          $[16] = t9;
          $[17] = t11;
        } else {
          t11 = $[17];
        }
        return t11;
      }
    case "select":
      {
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        const t4 = field.label || "";
        const t5 = field.placeholder || "Select...";
        const t6 = String(value ?? "");
        let t7;
        if ($[18] !== handleChange) {
          t7 = selectedValue_0 => handleChange(selectedValue_0);
          $[18] = handleChange;
          $[19] = t7;
        } else {
          t7 = $[19];
        }
        let t8;
        if ($[20] !== field.options) {
          t8 = field.options || [];
          $[20] = field.options;
          $[21] = t8;
        } else {
          t8 = $[21];
        }
        let t9;
        if ($[22] !== t8) {
          t9 = t8.filter(_temp);
          $[22] = t8;
          $[23] = t9;
        } else {
          t9 = $[23];
        }
        const t10 = field.required ?? undefined;
        let t11;
        if ($[24] !== descriptionNode || $[25] !== error || $[26] !== handleBlur || $[27] !== t10 || $[28] !== t2 || $[29] !== t3 || $[30] !== t4 || $[31] !== t5 || $[32] !== t6 || $[33] !== t7 || $[34] !== t9) {
          t11 = <Select id={t2} name={t3} label={t4} placeholder={t5} helperText={descriptionNode} errorText={error} value={t6} onChange={t7} onBlur={handleBlur} options={t9} required={t10} />;
          $[24] = descriptionNode;
          $[25] = error;
          $[26] = handleBlur;
          $[27] = t10;
          $[28] = t2;
          $[29] = t3;
          $[30] = t4;
          $[31] = t5;
          $[32] = t6;
          $[33] = t7;
          $[34] = t9;
          $[35] = t11;
        } else {
          t11 = $[35];
        }
        return t11;
      }
    case "multiSelect":
      {
        let t2;
        if ($[36] !== value) {
          t2 = Array.isArray(value) ? value.filter(_temp2) : [];
          $[36] = value;
          $[37] = t2;
        } else {
          t2 = $[37];
        }
        const selectedValues_0 = t2;
        const t3 = field.fieldName || name;
        const t4 = field.fieldName || name;
        const t5 = field.label || "";
        const t6 = field.placeholder || "Select...";
        let t7;
        if ($[38] !== handleChange) {
          t7 = next_3 => handleChange(next_3);
          $[38] = handleChange;
          $[39] = t7;
        } else {
          t7 = $[39];
        }
        let t8;
        if ($[40] !== field.options) {
          t8 = field.options || [];
          $[40] = field.options;
          $[41] = t8;
        } else {
          t8 = $[41];
        }
        let t9;
        if ($[42] !== t8) {
          t9 = t8.filter(_temp3);
          $[42] = t8;
          $[43] = t9;
        } else {
          t9 = $[43];
        }
        const t10 = field.required ?? undefined;
        let t11;
        if ($[44] !== descriptionNode || $[45] !== error || $[46] !== handleBlur || $[47] !== selectedValues_0 || $[48] !== t10 || $[49] !== t3 || $[50] !== t4 || $[51] !== t5 || $[52] !== t6 || $[53] !== t7 || $[54] !== t9) {
          t11 = <MultiSelect id={t3} name={t4} label={t5} placeholder={t6} helperText={descriptionNode} errorText={error} value={selectedValues_0} onChange={t7} onBlur={handleBlur} options={t9} required={t10} />;
          $[44] = descriptionNode;
          $[45] = error;
          $[46] = handleBlur;
          $[47] = selectedValues_0;
          $[48] = t10;
          $[49] = t3;
          $[50] = t4;
          $[51] = t5;
          $[52] = t6;
          $[53] = t7;
          $[54] = t9;
          $[55] = t11;
        } else {
          t11 = $[55];
        }
        return t11;
      }
    case "phone":
      {
        const t2 = field.fieldName || name;
        const t3 = field.label || "";
        const t4 = field.placeholder || undefined;
        const t5 = String(value ?? "");
        let t6;
        if ($[56] !== handleChange) {
          t6 = phoneValue => handleChange(phoneValue);
          $[56] = handleChange;
          $[57] = t6;
        } else {
          t6 = $[57];
        }
        const t7 = field.required ?? undefined;
        let t8;
        if ($[58] !== descriptionNode || $[59] !== error || $[60] !== handleBlur || $[61] !== t2 || $[62] !== t3 || $[63] !== t4 || $[64] !== t5 || $[65] !== t6 || $[66] !== t7) {
          t8 = <InputPhoneNumber name={t2} label={t3} placeholder={t4} description={descriptionNode} error={error} value={t5} onChange={t6} onBlur={handleBlur} country="za" enableSearch={true} required={t7} />;
          $[58] = descriptionNode;
          $[59] = error;
          $[60] = handleBlur;
          $[61] = t2;
          $[62] = t3;
          $[63] = t4;
          $[64] = t5;
          $[65] = t6;
          $[66] = t7;
          $[67] = t8;
        } else {
          t8 = $[67];
        }
        return t8;
      }
    case "radio":
      {
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        const t4 = field.label || "";
        let t5;
        if ($[68] !== field.options) {
          t5 = field.options || [];
          $[68] = field.options;
          $[69] = t5;
        } else {
          t5 = $[69];
        }
        let t6;
        if ($[70] !== t5) {
          t6 = t5.filter(_temp4);
          $[70] = t5;
          $[71] = t6;
        } else {
          t6 = $[71];
        }
        const t7 = String(value ?? "");
        let t8;
        if ($[72] !== handleChange) {
          t8 = selectedValue => handleChange(selectedValue);
          $[72] = handleChange;
          $[73] = t8;
        } else {
          t8 = $[73];
        }
        const t9 = field.required ?? undefined;
        let t10;
        if ($[74] !== descriptionNode || $[75] !== error || $[76] !== handleBlur || $[77] !== t2 || $[78] !== t3 || $[79] !== t4 || $[80] !== t6 || $[81] !== t7 || $[82] !== t8 || $[83] !== t9) {
          t10 = <RadioGroup id={t2} name={t3} label={t4} options={t6} value={t7} onChange={t8} onBlur={handleBlur} helperText={descriptionNode} errorText={error} required={t9} />;
          $[74] = descriptionNode;
          $[75] = error;
          $[76] = handleBlur;
          $[77] = t2;
          $[78] = t3;
          $[79] = t4;
          $[80] = t6;
          $[81] = t7;
          $[82] = t8;
          $[83] = t9;
          $[84] = t10;
        } else {
          t10 = $[84];
        }
        return t10;
      }
    case "checkbox":
      {
        let descId;
        let t2;
        let t3;
        let t4;
        let t5;
        let t6;
        if ($[85] !== error || $[86] !== field.fieldName || $[87] !== field.label || $[88] !== field.options || $[89] !== field.required || $[90] !== handleBlur || $[91] !== handleChange || $[92] !== hasDescription || $[93] !== name || $[94] !== value) {
          const selectedValues = Array.isArray(value) ? value.filter(_temp5) : [];
          descId = `${field.fieldName || name}-desc`;
          t2 = "flex w-full flex-col gap-2";
          t3 = error || hasDescription ? descId : undefined;
          t4 = !!error || undefined;
          if ($[101] !== field.label || $[102] !== field.required) {
            t5 = field.label && <legend className="mb-2 typography-component-sm-medium desktop:typography-component-md-medium text-input-text-label">{field.label}<RequiredMark required={field.required ?? undefined} /></legend>;
            $[101] = field.label;
            $[102] = field.required;
            $[103] = t5;
          } else {
            t5 = $[103];
          }
          t6 = (field.options || []).filter(_temp6).map(option_0 => <Checkbox key={option_0.value} name={field.fieldName || name} label={option_0.label} value={option_0.value} checked={selectedValues.includes(option_0.value)} onChange={checked_2 => {
            const next_2 = checked_2 ? [...selectedValues, option_0.value] : selectedValues.filter(v => v !== option_0.value);
            handleChange(next_2);
          }} onBlur={handleBlur} />);
          $[85] = error;
          $[86] = field.fieldName;
          $[87] = field.label;
          $[88] = field.options;
          $[89] = field.required;
          $[90] = handleBlur;
          $[91] = handleChange;
          $[92] = hasDescription;
          $[93] = name;
          $[94] = value;
          $[95] = descId;
          $[96] = t2;
          $[97] = t3;
          $[98] = t4;
          $[99] = t5;
          $[100] = t6;
        } else {
          descId = $[95];
          t2 = $[96];
          t3 = $[97];
          t4 = $[98];
          t5 = $[99];
          t6 = $[100];
        }
        const t7 = error ? "alert" : undefined;
        const t8 = error ? "typography-component-sm-regular desktop:typography-component-md-regular text-input-text-error min-h-[1lh]" : hasDescription ? "typography-component-sm-regular desktop:typography-component-md-regular text-input-text-label min-h-[1lh]" : "invisible min-h-[1lh]";
        const t9 = error || descriptionNode || "\xA0";
        let t10;
        if ($[104] !== descId || $[105] !== t7 || $[106] !== t8 || $[107] !== t9) {
          t10 = <p id={descId} role={t7} className={t8}>{t9}</p>;
          $[104] = descId;
          $[105] = t7;
          $[106] = t8;
          $[107] = t9;
          $[108] = t10;
        } else {
          t10 = $[108];
        }
        let t11;
        if ($[109] !== t10 || $[110] !== t2 || $[111] !== t3 || $[112] !== t4 || $[113] !== t5 || $[114] !== t6) {
          t11 = <fieldset className={t2} aria-describedby={t3} aria-invalid={t4}>{t5}{t6}{t10}</fieldset>;
          $[109] = t10;
          $[110] = t2;
          $[111] = t3;
          $[112] = t4;
          $[113] = t5;
          $[114] = t6;
          $[115] = t11;
        } else {
          t11 = $[115];
        }
        return t11;
      }
    case "file" as typeof field.type:
      {
        const fileField = field as unknown as {
          helperText?: string | null;
          acceptedFileTypes?: string[] | null;
          maxFileSizeMb?: number | null;
          multiple?: boolean | null;
          maxFiles?: number | null;
        };
        const acceptedMimeTypes = fileField.acceptedFileTypes && fileField.acceptedFileTypes.length > 0 ? fileField.acceptedFileTypes : undefined;
        const maxSizeMb = fileField.maxFileSizeMb ?? undefined;
        const multiple = fileField.multiple ?? false;
        const fileValue = Array.isArray(value) ? value as string[] : typeof value === "string" ? value : "";
        const t2 = field.fieldName || name;
        const t3 = field.label || "";
        const t4 = fileField.helperText ?? undefined;
        let t5;
        if ($[116] !== handleChange) {
          t5 = next_1 => handleChange(next_1);
          $[116] = handleChange;
          $[117] = t5;
        } else {
          t5 = $[117];
        }
        const t6 = field.required ?? undefined;
        const t7 = fileField.maxFiles ?? undefined;
        let t8;
        if ($[118] !== acceptedMimeTypes || $[119] !== descriptionNode || $[120] !== error || $[121] !== fileValue || $[122] !== formId || $[123] !== formWizardId || $[124] !== handleBlur || $[125] !== maxSizeMb || $[126] !== multiple || $[127] !== t2 || $[128] !== t3 || $[129] !== t4 || $[130] !== t5 || $[131] !== t6 || $[132] !== t7) {
          t8 = <InputFile name={t2} label={t3} description={descriptionNode} helperText={t4} value={fileValue} onChange={t5} onBlur={handleBlur} required={t6} error={error} formId={formId} formWizardId={formWizardId} acceptedMimeTypes={acceptedMimeTypes} maxSizeMb={maxSizeMb} multiple={multiple} maxFiles={t7} />;
          $[118] = acceptedMimeTypes;
          $[119] = descriptionNode;
          $[120] = error;
          $[121] = fileValue;
          $[122] = formId;
          $[123] = formWizardId;
          $[124] = handleBlur;
          $[125] = maxSizeMb;
          $[126] = multiple;
          $[127] = t2;
          $[128] = t3;
          $[129] = t4;
          $[130] = t5;
          $[131] = t6;
          $[132] = t7;
          $[133] = t8;
        } else {
          t8 = $[133];
        }
        return t8;
      }
    case "selectionCard" as typeof field.type:
      {
        const isSelected = Boolean(value);
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        const t4 = field.label || "";
        let t5;
        if ($[134] !== handleChange) {
          t5 = checked_1 => handleChange(checked_1);
          $[134] = handleChange;
          $[135] = t5;
        } else {
          t5 = $[135];
        }
        const t6 = field.required ?? undefined;
        let t7;
        if ($[136] !== descriptionNode || $[137] !== error || $[138] !== handleBlur || $[139] !== isSelected || $[140] !== t2 || $[141] !== t3 || $[142] !== t4 || $[143] !== t5 || $[144] !== t6) {
          t7 = <InputSelectionCard id={t2} name={t3} label={t4} description={descriptionNode} checked={isSelected} onChange={t5} onBlur={handleBlur} required={t6} error={error} />;
          $[136] = descriptionNode;
          $[137] = error;
          $[138] = handleBlur;
          $[139] = isSelected;
          $[140] = t2;
          $[141] = t3;
          $[142] = t4;
          $[143] = t5;
          $[144] = t6;
          $[145] = t7;
        } else {
          t7 = $[145];
        }
        return t7;
      }
    case "newsletterCheckbox":
      {
        const isNewsletterChecked = Boolean(value);
        const newsletterLabel = field.options?.[0]?.label || field.label || "Subscribe to Newsletter";
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        let t4;
        if ($[146] !== handleChange) {
          t4 = checked_0 => handleChange(checked_0);
          $[146] = handleChange;
          $[147] = t4;
        } else {
          t4 = $[147];
        }
        const t5 = field.required ?? undefined;
        let t6;
        if ($[148] !== descriptionNode || $[149] !== error || $[150] !== handleBlur || $[151] !== isNewsletterChecked || $[152] !== newsletterLabel || $[153] !== t2 || $[154] !== t3 || $[155] !== t4 || $[156] !== t5) {
          t6 = <Checkbox id={t2} name={t3} label={newsletterLabel} checked={isNewsletterChecked} onChange={t4} onBlur={handleBlur} helperText={descriptionNode} errorText={error} required={t5} />;
          $[148] = descriptionNode;
          $[149] = error;
          $[150] = handleBlur;
          $[151] = isNewsletterChecked;
          $[152] = newsletterLabel;
          $[153] = t2;
          $[154] = t3;
          $[155] = t4;
          $[156] = t5;
          $[157] = t6;
        } else {
          t6 = $[157];
        }
        return t6;
      }
    case "popiConsentCheckbox" as typeof field.type:
      {
        const isConsentChecked = Boolean(value);
        let t2;
        if ($[158] !== field) {
          t2 = getPopiConsentText(field);
          $[158] = field;
          $[159] = t2;
        } else {
          t2 = $[159];
        }
        const consentLabel = t2;
        const t3 = field.fieldName || name;
        const t4 = field.fieldName || name;
        let t5;
        if ($[160] !== handleChange) {
          t5 = checked => handleChange(checked);
          $[160] = handleChange;
          $[161] = t5;
        } else {
          t5 = $[161];
        }
        const t6 = field.required ?? undefined;
        let t7;
        if ($[162] !== consentLabel || $[163] !== descriptionNode || $[164] !== error || $[165] !== handleBlur || $[166] !== isConsentChecked || $[167] !== t3 || $[168] !== t4 || $[169] !== t5 || $[170] !== t6) {
          t7 = <Checkbox id={t3} name={t4} label={consentLabel} checked={isConsentChecked} onChange={t5} onBlur={handleBlur} helperText={descriptionNode} errorText={error} required={t6} />;
          $[162] = consentLabel;
          $[163] = descriptionNode;
          $[164] = error;
          $[165] = handleBlur;
          $[166] = isConsentChecked;
          $[167] = t3;
          $[168] = t4;
          $[169] = t5;
          $[170] = t6;
          $[171] = t7;
        } else {
          t7 = $[171];
        }
        return t7;
      }
    case "date":
      {
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        const t4 = field.label || "";
        const t5 = field.placeholder ?? undefined;
        const t6 = value as string | undefined ?? "";
        let t7;
        if ($[172] !== handleChange) {
          t7 = iso => handleChange(iso);
          $[172] = handleChange;
          $[173] = t7;
        } else {
          t7 = $[173];
        }
        const t8 = field.required ?? undefined;
        const t9 = field.minDate ?? undefined;
        const t10 = field.maxDate ?? undefined;
        let t11;
        if ($[174] !== descriptionNode || $[175] !== error || $[176] !== handleBlur || $[177] !== t10 || $[178] !== t2 || $[179] !== t3 || $[180] !== t4 || $[181] !== t5 || $[182] !== t6 || $[183] !== t7 || $[184] !== t8 || $[185] !== t9) {
          t11 = <InputDate id={t2} name={t3} label={t4} placeholder={t5} description={descriptionNode} error={error} value={t6} onChange={t7} onBlur={handleBlur} required={t8} minDate={t9} maxDate={t10} />;
          $[174] = descriptionNode;
          $[175] = error;
          $[176] = handleBlur;
          $[177] = t10;
          $[178] = t2;
          $[179] = t3;
          $[180] = t4;
          $[181] = t5;
          $[182] = t6;
          $[183] = t7;
          $[184] = t8;
          $[185] = t9;
          $[186] = t11;
        } else {
          t11 = $[186];
        }
        return t11;
      }
    case "dateRange":
      {
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        const t4 = field.label || "";
        const t5 = value as string | undefined ?? "";
        let t6;
        if ($[187] !== handleChange) {
          t6 = range => handleChange(range);
          $[187] = handleChange;
          $[188] = t6;
        } else {
          t6 = $[188];
        }
        const t7 = field.required ?? undefined;
        const t8 = field.minDate ?? undefined;
        const t9 = field.maxDate ?? undefined;
        let t10;
        if ($[189] !== descriptionNode || $[190] !== error || $[191] !== handleBlur || $[192] !== t2 || $[193] !== t3 || $[194] !== t4 || $[195] !== t5 || $[196] !== t6 || $[197] !== t7 || $[198] !== t8 || $[199] !== t9) {
          t10 = <InputDateRange id={t2} name={t3} label={t4} description={descriptionNode} error={error} value={t5} onChange={t6} onBlur={handleBlur} required={t7} minDate={t8} maxDate={t9} />;
          $[189] = descriptionNode;
          $[190] = error;
          $[191] = handleBlur;
          $[192] = t2;
          $[193] = t3;
          $[194] = t4;
          $[195] = t5;
          $[196] = t6;
          $[197] = t7;
          $[198] = t8;
          $[199] = t9;
          $[200] = t10;
        } else {
          t10 = $[200];
        }
        return t10;
      }
    case "time":
      {
        const interval = field.minuteInterval as 1 | 5 | 15 | 30 | undefined;
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        const t4 = field.label || "";
        const t5 = value as string | undefined ?? "";
        let t6;
        if ($[201] !== handleChange) {
          t6 = time => handleChange(time);
          $[201] = handleChange;
          $[202] = t6;
        } else {
          t6 = $[202];
        }
        const t7 = field.required ?? undefined;
        const t8 = interval ?? 15;
        let t9;
        if ($[203] !== descriptionNode || $[204] !== error || $[205] !== handleBlur || $[206] !== t2 || $[207] !== t3 || $[208] !== t4 || $[209] !== t5 || $[210] !== t6 || $[211] !== t7 || $[212] !== t8) {
          t9 = <InputTime id={t2} name={t3} label={t4} description={descriptionNode} error={error} value={t5} onChange={t6} onBlur={handleBlur} required={t7} minuteInterval={t8} />;
          $[203] = descriptionNode;
          $[204] = error;
          $[205] = handleBlur;
          $[206] = t2;
          $[207] = t3;
          $[208] = t4;
          $[209] = t5;
          $[210] = t6;
          $[211] = t7;
          $[212] = t8;
          $[213] = t9;
        } else {
          t9 = $[213];
        }
        return t9;
      }
    case "repeater" as typeof field.type:
      {
        const repeaterField = field as unknown as {
          columns?: RepeaterColumn[] | null;
          addButtonLabel?: string | null;
          maxRows?: number | null;
        };
        let t2;
        if ($[214] !== value) {
          t2 = Array.isArray(value) ? value as RepeaterRow[] : [];
          $[214] = value;
          $[215] = t2;
        } else {
          t2 = $[215];
        }
        const rows = t2;
        const t3 = field.fieldName || name;
        const t4 = field.label || "";
        let t5;
        if ($[216] !== repeaterField.columns) {
          t5 = repeaterField.columns ?? [];
          $[216] = repeaterField.columns;
          $[217] = t5;
        } else {
          t5 = $[217];
        }
        let t6;
        if ($[218] !== handleChange) {
          t6 = next_0 => handleChange(next_0);
          $[218] = handleChange;
          $[219] = t6;
        } else {
          t6 = $[219];
        }
        const t7 = field.required ?? undefined;
        const t8 = repeaterField.addButtonLabel ?? undefined;
        const t9 = repeaterField.maxRows ?? undefined;
        let t10;
        if ($[220] !== descriptionNode || $[221] !== error || $[222] !== handleBlur || $[223] !== rows || $[224] !== t3 || $[225] !== t4 || $[226] !== t5 || $[227] !== t6 || $[228] !== t7 || $[229] !== t8 || $[230] !== t9) {
          t10 = <InputRepeater name={t3} label={t4} description={descriptionNode} columns={t5} value={rows} onChange={t6} onBlur={handleBlur} required={t7} error={error} addButtonLabel={t8} maxRows={t9} />;
          $[220] = descriptionNode;
          $[221] = error;
          $[222] = handleBlur;
          $[223] = rows;
          $[224] = t3;
          $[225] = t4;
          $[226] = t5;
          $[227] = t6;
          $[228] = t7;
          $[229] = t8;
          $[230] = t9;
          $[231] = t10;
        } else {
          t10 = $[231];
        }
        return t10;
      }
    case "number":
      {
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        const t4 = field.label || "";
        const t5 = field.placeholder || undefined;
        const t6 = String(value ?? "");
        let t7;
        if ($[232] !== handleChange) {
          t7 = e_1 => {
            const next = e_1.target.value;
            if (/[+\-eE]/.test(next)) {
              return;
            }
            if (next !== "" && Number(next) < 0) {
              return;
            }
            handleChange(next);
          };
          $[232] = handleChange;
          $[233] = t7;
        } else {
          t7 = $[233];
        }
        const t8 = field.required ?? undefined;
        let t9;
        if ($[234] !== descriptionNode || $[235] !== error || $[236] !== handleBlur || $[237] !== t2 || $[238] !== t3 || $[239] !== t4 || $[240] !== t5 || $[241] !== t6 || $[242] !== t7 || $[243] !== t8) {
          t9 = <FormInput id={t2} name={t3} type="number" inputMode="numeric" min={0} label={t4} placeholder={t5} helperText={descriptionNode} errorText={error} value={t6} onKeyDown={_temp7} onChange={t7} onBlur={handleBlur} required={t8} />;
          $[234] = descriptionNode;
          $[235] = error;
          $[236] = handleBlur;
          $[237] = t2;
          $[238] = t3;
          $[239] = t4;
          $[240] = t5;
          $[241] = t6;
          $[242] = t7;
          $[243] = t8;
          $[244] = t9;
        } else {
          t9 = $[244];
        }
        return t9;
      }
    case "text":
    case "email":
    default:
      {
        const t2 = field.fieldName || name;
        const t3 = field.fieldName || name;
        const t4 = field.type as React.HTMLInputTypeAttribute;
        const t5 = field.label || "";
        const t6 = field.placeholder || undefined;
        const t7 = String(value ?? "");
        let t8;
        if ($[245] !== handleChange) {
          t8 = e => handleChange(e.target.value);
          $[245] = handleChange;
          $[246] = t8;
        } else {
          t8 = $[246];
        }
        const t9 = field.characterLimit || undefined;
        const t10 = field.required ?? undefined;
        let t11;
        if ($[247] !== descriptionNode || $[248] !== error || $[249] !== handleBlur || $[250] !== t10 || $[251] !== t2 || $[252] !== t3 || $[253] !== t4 || $[254] !== t5 || $[255] !== t6 || $[256] !== t7 || $[257] !== t8 || $[258] !== t9) {
          t11 = <FormInput id={t2} name={t3} type={t4} label={t5} placeholder={t6} helperText={descriptionNode} errorText={error} value={t7} onChange={t8} onBlur={handleBlur} maxLength={t9} required={t10} />;
          $[247] = descriptionNode;
          $[248] = error;
          $[249] = handleBlur;
          $[250] = t10;
          $[251] = t2;
          $[252] = t3;
          $[253] = t4;
          $[254] = t5;
          $[255] = t6;
          $[256] = t7;
          $[257] = t8;
          $[258] = t9;
          $[259] = t11;
        } else {
          t11 = $[259];
        }
        return t11;
      }
  }
};
function _temp(option_3) {
  return option_3?.label != null && option_3?.value != null;
}
function _temp2(item_0) {
  return typeof item_0 === "string";
}
function _temp3(option_2) {
  return option_2?.label != null && option_2?.value != null;
}
function _temp4(option_1) {
  return option_1?.label != null && option_1?.value != null;
}
function _temp5(item) {
  return typeof item === "string";
}
function _temp6(option) {
  return option?.label != null && option?.value != null;
}
function _temp7(e_0) {
  if (["-", "+", "e", "E"].includes(e_0.key)) {
    e_0.preventDefault();
  }
}